import { useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'

import { ArrowDownTrayIcon } from '@gto/icons-react/outline/arrow-down-tray'
import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { ClockIcon } from '@gto/icons-react/outline/clock'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { LockClosedIcon } from '@gto/icons-react/outline/lock-closed'
import { Button } from '@gto/ui/button'

import type { AffiliateAdminPayoutSchema, ListPayoutsParams } from '@/api/gen'
import {
  AffiliatePayoutStatus,
  getGetProgramStatsViewQueryKey,
  getListPayoutsQueryKey,
  useGetProgramStatsView,
  useListPayouts,
  useUpdatePayoutStatus,
} from '@/api/gen'
import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { EmptyState } from '@/components/empty-state'
import { InlineQueryError } from '@/components/inline-query-error'
import { Page } from '@/components/page'
import { PayoutDetailDialog } from '@/components/payout-detail-dialog'
import { PayoutsTable } from '@/components/payouts-table'
import { StatsCard } from '@/components/stats-card'
import { TableFilterBar } from '@/components/table-filter-bar'
import {
  formatPayoutDetails,
  isPayoutMethod,
  METHOD_LABELS,
  PAYOUT_METHODS,
} from '@/domain/payout-methods'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { rowsToCsv } from '@/utils/csv'
import { downloadBlob } from '@/utils/download-blob'
import { extractValidationDetail } from '@/utils/extract-validation-detail'
import { toAmount } from '@/utils/to-amount'

const STATUS_OPTION_KEYS = [
  { value: 'ALL', labelId: 'admin.payouts.status.all' },
  { value: 'REQUESTED', labelId: 'admin.payouts.status.requested' },
  { value: 'PAID', labelId: 'admin.payouts.status.paid' },
  { value: 'REJECTED', labelId: 'admin.payouts.status.rejected' },
  { value: 'CANCELLED', labelId: 'admin.payouts.status.cancelled' },
  { value: 'REVERSED', labelId: 'admin.payouts.status.reversed' },
] as const

// ─── CSV Export ─────────────────────────────────────────────────────────────

const exportPayoutsCsv = (payouts: AffiliateAdminPayoutSchema[]) => {
  const headers = ['Partner', 'Email', 'Amount', 'Method', 'Status', 'Requested', 'Payout Details']
  const rows = payouts.map((p) => [
    p.partner.display_name,
    p.partner.email,
    toAmount(p.requested_amount, 'payouts csv requested_amount:').toFixed(2),
    p.payout_method ?? '',
    p.status,
    new Date(p.created_at).toLocaleDateString('en-US'),
    formatPayoutDetails(p.payout_method ?? '', p.payout_details),
  ])

  const blob = new Blob([rowsToCsv([headers, ...rows])], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, `payouts-${new Date().toISOString().slice(0, 10)}.csv`)
}

const METHOD_FILTER_OPTIONS = PAYOUT_METHODS.map((method) => ({
  value: method,
  label: METHOD_LABELS[method],
}))

const isPayoutStatus = (value: string): value is AffiliatePayoutStatus =>
  Object.prototype.hasOwnProperty.call(AffiliatePayoutStatus, value)

// ─── Main Page ──────────────────────────────────────────────────────────────

export const AdminPayoutsPage = () => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()

  const STATUS_OPTIONS = STATUS_OPTION_KEYS.map((opt) => ({
    value: opt.value,
    label: formatMessage({ id: opt.labelId }),
  }))

  const [statusFilter, setStatusFilter] = useState('ALL')
  const [methodFilter, setMethodFilter] = useState('')
  const [search, setSearch] = useState('')
  const [selectedPayout, setSelectedPayout] = useState<AffiliateAdminPayoutSchema | null>(null)

  // Build the server params from the typed filters. Both `status` and
  // `payout_method` are now enum-typed on the wire, so narrow before assigning.
  // Omit the params object entirely when nothing is filtered so the query key
  // stays stable (== the old `undefined`).
  const params: ListPayoutsParams = {}
  if (statusFilter !== 'ALL' && isPayoutStatus(statusFilter)) {
    params.status = statusFilter
  }
  if (methodFilter !== '' && isPayoutMethod(methodFilter)) {
    params.payout_method = methodFilter
  }
  const apiParams = Object.keys(params).length > 0 ? params : undefined
  const payoutsQuery = useListPayouts(apiParams)
  const { data: allPayouts, isLoading, isError, refetch } = payoutsQuery

  // Server snapshot for the four finance tiles. The endpoint returns *current*
  // balances (locked / available / open) and the size of the pending-payouts
  // inbox — none of which is scoped by the status filter applied to the queue
  // below, so the tiles stay stable as the operator filters the table.
  const statsQuery = useGetProgramStatsView()
  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats,
  } = statsQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  // Reports only on TERMINAL failure (no more retries) so an intermittent blip
  // with TanStack retries doesn't multiply Sentry events.
  useReportQueryError(payoutsQuery, 'listPayouts failed: ')
  useReportQueryError(statsQuery, 'getProgramStatsView failed: ')

  const payouts = allPayouts ?? []
  const q = search.toLowerCase()
  const filteredPayouts =
    search === ''
      ? payouts
      : payouts.filter(
          (p) =>
            p.partner.display_name.toLowerCase().includes(q) ||
            p.partner.email.toLowerCase().includes(q),
        )

  const pendingPayoutsCount = stats?.pending_payouts_count ?? 0
  const pendingPayoutsUsd = toAmount(
    stats?.pending_payouts_usd,
    'admin-payouts pending_payouts_usd:',
  )
  const totalOpenUsd = toAmount(stats?.total_open_usd, 'admin-payouts total_open_usd:')
  const totalAvailableUsd = toAmount(
    stats?.total_available_usd,
    'admin-payouts total_available_usd:',
  )
  const totalLockedUsd = toAmount(stats?.total_locked_usd, 'admin-payouts total_locked_usd:')

  // Money-critical: a failed approve (→ PAID) / reject / cancel / reverse must
  // reach Sentry AND surface to the operator inside the dialog, and must NOT fire
  // the success side-effect (close the dialog + invalidate). A reverse 422 (blank
  // reason / non-reversible payout) carries an actionable Ninja detail, so pull
  // it out for the inline alert instead of the generic fallback.
  const actionError = useMutationErrorHandler('updatePayoutStatus failed: ', {
    extractDetail: extractValidationDetail,
  })

  const updatePayout = useUpdatePayoutStatus({
    mutation: {
      onMutate: actionError.clearError,
      onSuccess: () => {
        // Every status transition (approve/reject/cancel/reverse) moves money
        // between the locked / available / open / paid buckets, so refresh both
        // the queue and the finance tiles — a cancel or reverse returns the
        // amount to Open Balance and it must reflect immediately.
        void queryClient.invalidateQueries({ queryKey: getListPayoutsQueryKey(apiParams) })
        void queryClient.invalidateQueries({ queryKey: getGetProgramStatsViewQueryKey() })
        setSelectedPayout(null)
      },
      onError: actionError.onError,
    },
  })

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage id="admin.payouts.title" />}
        actions={
          <Button
            disabled={filteredPayouts.length === 0}
            variant="contained"
            startIcon={
              <ArrowDownTrayIcon
                height={18}
                width={18}
              />
            }
            onClick={() => exportPayoutsCsv(filteredPayouts)}
          >
            <FormattedMessage id="admin.payouts.exportCsv" />
          </Button>
        }
      />

      <Page.Content>
        {isError ? (
          <InlineQueryError
            data-tst="payouts-error"
            messageId="admin.payouts.error"
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            {statsError ? (
              <InlineQueryError
                data-tst="payouts-stats-error"
                messageId="admin.payouts.error"
                onRetry={() => void refetchStats()}
              />
            ) : (
              <div
                className="flex flex-wrap gap-2"
                data-tst="kpi-strip"
              >
                <DashboardKpiTile
                  icon={<ClockIcon />}
                  loading={statsLoading}
                  tooltip={formatMessage({ id: 'tooltip.pendingPayoutsAmount' })}
                  label={
                    <FormattedMessage
                      id="admin.payouts.pendingPayouts.label"
                      values={{ count: pendingPayoutsCount }}
                    />
                  }
                  value={
                    <FormattedNumber
                      currency="USD"
                      style="currency"
                      value={pendingPayoutsUsd}
                    />
                  }
                />
                <DashboardKpiTile
                  icon={<CurrencyDollarIcon />}
                  label={<FormattedMessage id="admin.payouts.totalLiability" />}
                  loading={statsLoading}
                  tooltip={formatMessage({ id: 'tooltip.totalLiability' })}
                  value={
                    <FormattedNumber
                      currency="USD"
                      style="currency"
                      value={totalOpenUsd}
                    />
                  }
                />
                <DashboardKpiTile
                  icon={<BanknotesIcon />}
                  label={<FormattedMessage id="admin.payouts.availableNow" />}
                  loading={statsLoading}
                  tooltip={formatMessage({ id: 'tooltip.availableNow' })}
                  value={
                    <FormattedNumber
                      currency="USD"
                      style="currency"
                      value={totalAvailableUsd}
                    />
                  }
                />
                <DashboardKpiTile
                  icon={<LockClosedIcon />}
                  label={<FormattedMessage id="admin.payouts.locked" />}
                  loading={statsLoading}
                  tooltip={formatMessage({ id: 'tooltip.lockedBalance' })}
                  value={
                    <FormattedNumber
                      currency="USD"
                      style="currency"
                      value={totalLockedUsd}
                    />
                  }
                />
              </div>
            )}

            <TableFilterBar
              options={STATUS_OPTIONS}
              search={search}
              searchPlaceholder={formatMessage({ id: 'admin.payouts.searchPartner' })}
              selectAllLabel={formatMessage({ id: 'admin.payouts.method.all' })}
              selectLabel={formatMessage({ id: 'admin.payouts.filterMethod' })}
              selectOptions={METHOD_FILTER_OPTIONS}
              selectValue={methodFilter}
              value={statusFilter}
              onSearchChange={setSearch}
              onSelectChange={setMethodFilter}
              onValueChange={setStatusFilter}
            />

            <StatsCard
              icon={<BanknotesIcon />}
              title={<FormattedMessage id="admin.payouts.queueTitle" />}
            >
              {!isLoading && filteredPayouts.length === 0 ? (
                <EmptyState title={<FormattedMessage id="admin.payouts.empty" />} />
              ) : (
                <PayoutsTable
                  showPartner
                  showResolvedBy
                  data={filteredPayouts}
                  getRowId={(row) => row.id}
                  loading={isLoading}
                  onRowClick={(clicked) => {
                    // Look up the FULL admin row from the source list rather
                    // than read it from the clicked row — the table is pinned to
                    // the structural `PayoutRow`, so admin-only fields the
                    // dialog needs (`payout_details`, `original_amount`,
                    // `resolved_by_email`) wouldn't survive the narrowing.
                    const fullRow = filteredPayouts.find((p) => p.id === clicked.id)
                    if (fullRow == null) {
                      return
                    }
                    actionError.clearError()
                    setSelectedPayout(fullRow)
                  }}
                />
              )}
            </StatsCard>
          </>
        )}
      </Page.Content>

      {selectedPayout && (
        <PayoutDetailDialog
          actionError={actionError}
          loading={updatePayout.isPending}
          payout={selectedPayout}
          onClose={() => setSelectedPayout(null)}
          onAmountAdjusted={(updated) => {
            actionError.clearError()
            setSelectedPayout(updated)
            void queryClient.invalidateQueries({ queryKey: getListPayoutsQueryKey(apiParams) })
          }}
          onApprove={(payout) => {
            updatePayout.mutate({
              payoutId: payout.id,
              data: { status: 'PAID' },
            })
          }}
          onCancel={(payout) => {
            updatePayout.mutate({
              payoutId: payout.id,
              data: { status: 'CANCELLED' },
            })
          }}
          onReject={(payout) => {
            updatePayout.mutate({
              payoutId: payout.id,
              data: { status: 'REJECTED' },
            })
          }}
          onReverse={(payout, reason) => {
            updatePayout.mutate({
              payoutId: payout.id,
              data: { status: 'REVERSED', reason },
            })
          }}
        />
      )}
    </Page>
  )
}
